import { useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { trpc } from "../trpc";
import { StorageZap } from "./useQueryStorageZaps";
import { OrganizationZaps } from "./useQueryOrganizationZaps";

interface Input {
  folderId?: string; // used for cache invalidation
}
export function useChangeOwner(input?: Input) {
  const { folderId } = input || {};

  const utils = trpc.useUtils();

  const queryClient = useQueryClient();
  const storageKey = getQueryKey(trpc.zap.storageZaps, undefined, "any");
  const orgKey = getQueryKey(trpc.zap.organizationZaps, undefined, "any");
  const searchKey = getQueryKey(trpc.zap.searchZaps, undefined, "any");

  const { mutate, isPending } = trpc.zap.changeOwner.useMutation({
    // Callback to handle optimistic updates in RQ cache
    onMutate: ({ zapIds, ownerId }) => {
      queryClient.cancelQueries({ queryKey: storageKey });
      queryClient.cancelQueries({ queryKey: orgKey });
      queryClient.setQueriesData(
        {
          queryKey: storageKey,
          type: "active",
        },
        (old: Record<string, StorageZap> | undefined) => {
          if (!old) {
            return old;
          }
          const updated = { ...old };
          zapIds.forEach((id) => {
            if (updated?.[id]) {
              updated[id] = {
                ...updated[id],
                customuserId: Number(ownerId) || 0,
              };
            }
            return;
          });
          return updated;
        },
      );
      queryClient.setQueriesData(
        {
          queryKey: orgKey,
          type: "active",
        },
        (old: OrganizationZaps | undefined) => {
          if (!old) {
            return old;
          }
          const zapIdsSet = new Set(zapIds);
          if (old?.results?.some((zap) => zapIdsSet.has(String(zap.id)))) {
            return {
              ...old,
              results: old.results.map((zap) => {
                if (zapIdsSet.has(String(zap.id))) {
                  return {
                    ...zap,
                    customuserId: Number(ownerId) || 0,
                  };
                }
                return zap;
              }),
            };
          }
        },
      );
    },
    // Callback to handle cache invalidation after mutation is completed
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: storageKey });
      queryClient.invalidateQueries({ queryKey: orgKey });
      queryClient.invalidateQueries({ queryKey: searchKey });
      if (folderId) {
        utils.asset.getHydratedFolderAssets.invalidate({
          folderId,
        });
      }

      // Invalidate permissions cache so kebab menu reflects new owner's permissions
      utils.permission.checkPermissions.invalidate();
      utils.permission.zap.invalidate();
    },
  });

  return {
    changeOwner: mutate,
    isLoading: isPending,
  };
}
